import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AuthRedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasHandledRedirect = useRef(false);
  const isManualLogin = useRef(false);

  // Track if user is on login page (manual login scenario)
  useEffect(() => {
    isManualLogin.current = location.pathname === '/login';
  }, [location.pathname]);

  useEffect(() => {
    // Parse URL hash for Supabase tokens (indicates email link click)
    const parseUrlHash = () => {
      const hash = window.location.hash;
      if (!hash) return null;

      const params = new URLSearchParams(hash.substring(1));
      return {
        accessToken: params.get('access_token'),
        type: params.get('type'),
      };
    };

    // Clean up URL hash after processing
    const cleanupHash = () => {
      if (window.location.hash) {
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.replaceState(null, '', cleanUrl);
      }
    };

    const handleAuthStateChange = (event, session) => {
      // Skip if we've already handled a redirect in this session
      if (hasHandledRedirect.current) return;

      // Skip INITIAL_SESSION - user already has session, just loading page
      if (event === 'INITIAL_SESSION') return;

      const hashParams = parseUrlHash();
      const hasHashToken = !!hashParams?.accessToken;

      if (import.meta.env.DEV) {
        console.log('AuthRedirectHandler:', {
          event,
          hasSession: !!session,
          hasHashToken,
          hashType: hashParams?.type,
          currentPath: location.pathname,
          isManualLogin: isManualLogin.current
        });
      }

      // Handle PASSWORD_RECOVERY event
      if (event === 'PASSWORD_RECOVERY') {
        if (location.pathname !== '/reset-password') {
          hasHandledRedirect.current = true;
          cleanupHash();
          navigate('/reset-password', { replace: true });
        }
        return;
      }

      // Handle SIGNED_IN event
      if (event === 'SIGNED_IN' && session) {
        // Check if this is from an email link (has hash tokens)
        if (hasHashToken) {
          // Handle invite flow (vet invitation) - they need to set their password
          if (hashParams?.type === 'invite') {
            if (location.pathname !== '/update-password') {
              hasHandledRedirect.current = true;
              cleanupHash();
              navigate('/update-password', { replace: true });
              return;
            }
          }

          // Handle signup confirmation - user already has password, go to login
          // Also handles magic link sign ins
          if (location.pathname !== '/login' && location.pathname !== '/dashboard' && location.pathname !== '/update-password') {
            hasHandledRedirect.current = true;
            cleanupHash();
            navigate('/login?email_confirmed=true', { replace: true });
          }
          return;
        }

        // If on login page and signing in, LoginPage handles its own redirect
        // Don't interfere with manual logins
        if (isManualLogin.current) {
          return;
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    // Check immediately on mount for hash params (handles case where Supabase processed token before listener attached)
    const initialHashParams = parseUrlHash();
    if (initialHashParams?.type === 'recovery' && location.pathname !== '/reset-password') {
      hasHandledRedirect.current = true;
      navigate('/reset-password', { replace: true });
    }
    // Only redirect invited vets to update-password (they need to set their password)
    // Regular signup users already set their password during signup, so they go to /login
    if (initialHashParams?.type === 'invite' && location.pathname !== '/update-password') {
      hasHandledRedirect.current = true;
      navigate('/update-password', { replace: true });
    }
    if (initialHashParams?.type === 'signup' && location.pathname !== '/login' && location.pathname !== '/dashboard') {
      hasHandledRedirect.current = true;
      navigate('/login?email_confirmed=true', { replace: true });
    }

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  return null;
};

export default AuthRedirectHandler;
